import React, { useState } from "react";

interface AddFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    file: File | null,
    expiryDate: string,
    reminder: string
  ) => void;
  folderId: string;
}

const AddFileModal: React.FC<AddFileModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  folderId,
}) => {
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [reminder, setReminder] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  React.useEffect(() => {
    if (isOpen && folderId) {
      setExpiryDate("");
      setReminder("");
      setFile(null);
      setErrorMessage("");
    }
  }, [isOpen, folderId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setErrorMessage("");
    }
  };

  const handleUpload = () => {
    if (file && file.size > 5 * 1024 * 1024) {
      setErrorMessage("File size cannot be more than 5MB");
      return;
    }
    onUpload(file, expiryDate, reminder);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1000001]">
      <div className="bg-white rounded-lg shadow-lg w-[800px]">
        <div className="p-6 overflow-hidden">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">Add Files</h2>
            <hr className="my-2 border-gray-300" />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="w-full">
              <label className="block text-gray-600 font-semibold">
                Expiry Date
              </label>
              <input
                type="date"
                className="w-full border rounded-md p-2"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-gray-600 font-semibold">
                Reminder Days Before
              </label>
              <select
                className="w-full border rounded-md p-2"
                onChange={(e) => setReminder(e.target.value)}
              >
                <option value="">Select</option>
                <option value="1 Week">1 Week</option>
                <option value="2 Weeks">2 Weeks</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-semibold">Upload Files</label>
            <div className="mb-4 border-dashed border-2 p-6 text-center cursor-pointer">
              <input
                type="file"
                className="hidden"
                id="fileInput"
                accept=".docx,.pdf,.txt,.jpg,.png,.ppt,.pptx"
                onChange={handleFileChange}
              />
              <label htmlFor="fileInput" className="cursor-pointer">
                {file ? (
                  <p className="text-gray-600">{file.name}</p>
                ) : (
                  "Drag & Drop Your Files Here Or Click"
                )}
              </label>
              <p className="text-sm text-gray-500 mt-2">
                Supported formats: DOCX, XLSX, PDF, TXT, JPG, PNG, PPT, PPTX
                (Max: 5MB)
              </p>
            </div>
            {errorMessage && (
              <p className="text-red-600 text-sm">{errorMessage}</p>
            )}
          </div>
        </div>
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-400 text-white rounded-full"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-teal-500 text-white rounded-full"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFileModal;
